import { Injectable } from '@angular/core';
import type { ReportDescriptorInterface } from '@zaoblako/shared/common/dtos/report/report-descriptor.dto';
import type { HikvisionCameraEventFindInterface } from '@zaoblako/shared/modules/cctv/dtos/hikvision-camera-event/hikvision-camera-event-find.dto';
import type { HikvisionCameraEventFormInterface } from '@zaoblako/shared/modules/cctv/dtos/hikvision-camera-event/hikvision-camera-event-form.dto';
import type { HikvisionCameraEventPageInterface } from '@zaoblako/shared/modules/cctv/dtos/hikvision-camera-event/hikvision-camera-event-page.dto';
import type { HikvisionCameraEventQueryInterface } from '@zaoblako/shared/modules/cctv/dtos/hikvision-camera-event/hikvision-camera-event-query.dto';
import type { HikvisionCameraEventWrongCategoryFormInterface } from '@zaoblako/shared/modules/cctv/dtos/hikvision-camera-event/hikvision-camera-event-wrong-category-form.dto';
import type { HikvisionCameraEventGuestPassFormInterface } from '@zaoblako/shared/modules/cctv/dtos/hikvision-camera-event/hikvision-camera-event-guest-pass-form.dto';
import type { HikvisionCameraFindManyQueryInterface } from '@zaoblako/shared/modules/cctv/dtos/hikvision-camera/hikvision-camera-find-many-query.dto';
import type { HikvisionCameraPageInterface } from '@zaoblako/shared/modules/cctv/dtos/hikvision-camera/hikvision-camera-page.dto';
import type { HikvisionCameraFindInterface } from '@zaoblako/shared/modules/cctv/dtos/hikvision-camera/hikvision-camera-find.dto';
import type { PaginationQueryInterface } from '@zaoblako/shared/common/dtos/pagination-query.dto';
import type { HistoryPageInterface } from '@zaoblako/shared/common/dtos/history/history-page.dto';
import type { HikvisionCameraEventHistoryFindInterface } from '@zaoblako/shared/modules/cctv/dtos/hikvision-camera-event/hikvision-camera-event-history-find.dto';
import type { RtspStreamSettingsInterface } from '@zaoblako/shared/modules/rtsp-player/dtos/rtsp-stream-settings.dto';
import type { HikvisionCameraStreamChannelInterface } from '@zaoblako/shared/modules/cctv/dtos/hikvision-camera-stream-channel.dto';
import type { FilePageInterface } from '@zaoblako/shared/modules/storage/dtos/file/file-page.dto';
import type { HivisionCameraOpenBarrierResultInterface } from '@zaoblako/shared/modules/cctv/dtos/hikvision-camera/hivision-camera-open-barrier-result.dto';
import { BaseService } from '@zaoblako/web/core/services/base.service';
import { Observable } from 'rxjs';
import type { StringValue } from 'ms';

@Injectable({
  providedIn: 'root'
})
export class HikvisionCameraService extends BaseService {
  protected baseUrl = '/cctv/hikvision/camera';

  public getRegistryDescriptor(): Observable<ReportDescriptorInterface> {
    return this.http.get<ReportDescriptorInterface>(`${this.baseUrl}/registry-descriptor`);
  }

  public findPage(queryParams?: HikvisionCameraFindManyQueryInterface): Observable<HikvisionCameraPageInterface> {
    return this.http.get<HikvisionCameraPageInterface>(this.baseUrl, {
      params: queryParams ? this.objectToHttpParams(queryParams) : {}
    });
  }

  public restoreOne(id: number): Observable<HikvisionCameraFindInterface> {
    return this.http.post<HikvisionCameraFindInterface>(`${this.baseUrl}/${id}/restore`, null);
  }

  public openBarrierForOne(id: number): Observable<HivisionCameraOpenBarrierResultInterface> {
    return this.http.get<HivisionCameraOpenBarrierResultInterface>(`${this.baseUrl}/${id}/open-barrier`);
  }

  public getHistoryForOne(id: number, queryParams?: PaginationQueryInterface): Observable<HistoryPageInterface> {
    return this.http.get<HikvisionCameraPageInterface>(`${this.baseUrl}/${id}/history`, {
      params: queryParams ? this.objectToHttpParams(queryParams) : {}
    });
  }

  public getImageForOne(id: number): Observable<BlobPart> {
    return this.http.get<BlobPart>(`${this.baseUrl}/${id}/image`, {
      responseType: 'arraybuffer' as any
    });
  }

  public findEvents(queryParams: HikvisionCameraEventQueryInterface): Observable<HikvisionCameraEventPageInterface> {
    return this.http.get<HikvisionCameraEventPageInterface>(`${this.baseUrl}-event`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public partialUpdateOne(
    eventId: number,
    body: HikvisionCameraEventFormInterface
  ): Observable<HikvisionCameraEventFindInterface> {
    return this.http.patch<HikvisionCameraEventFindInterface>(`${this.baseUrl}-event/${eventId}`, body);
  }

  public partialUpdateWrongCategory(
    eventId: number,
    body: HikvisionCameraEventWrongCategoryFormInterface
  ): Observable<HikvisionCameraEventFindInterface> {
    return this.http.patch<HikvisionCameraEventFindInterface>(`${this.baseUrl}-event/${eventId}/wrong-category`, body);
  }

  public createGuestPass(
    eventId: number,
    body: HikvisionCameraEventGuestPassFormInterface
  ): Observable<HikvisionCameraEventFindInterface> {
    return this.http.post<HikvisionCameraEventFindInterface>(`${this.baseUrl}-event/${eventId}/guest-pass`, body);
  }

  public openBarrierForEvent(eventId: number): Observable<HikvisionCameraEventFindInterface> {
    return this.http.get<HikvisionCameraEventFindInterface>(`${this.baseUrl}-event/${eventId}/open-barrier`);
  }

  public getEventHistory(eventId: number): Observable<HikvisionCameraEventHistoryFindInterface> {
    return this.http.get<HikvisionCameraEventHistoryFindInterface>(`${this.baseUrl}-event/${eventId}/history`);
  }

  public getAvailableStreamsForOne(id: number): Observable<HikvisionCameraStreamChannelInterface[]> {
    return this.http.get<HikvisionCameraStreamChannelInterface[]>(`${this.baseUrl}/${id}/available-streams`);
  }

  /** @deprecated */
  public getStreamSettingsForOne(id: number): Observable<RtspStreamSettingsInterface> {
    return this.http.get<RtspStreamSettingsInterface>(`${this.baseUrl}/${id}/stream-settings`);
  }

  public recordStreamForOne(id: number, duration: StringValue): Observable<null> {
    return this.http.get<null>(`${this.baseUrl}/${id}/record-stream`, {
      params: this.objectToHttpParams({ duration })
    });
  }

  public getStreamRecordingsForOne(id: number, queryParams?: PaginationQueryInterface): Observable<FilePageInterface> {
    return this.http.get<FilePageInterface>(`${this.baseUrl}/${id}/stream-recordings`, {
      params: queryParams ? this.objectToHttpParams(queryParams) : {}
    });
  }
}
